import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  DEFAULT_THEME_ID,
  THEME_LIST,
  applyThemeToDocument,
  getTheme,
  resolveThemeId,
} from '../themes';
import { useAuth } from './AuthContext';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

const themeStorageKey = (userKey) => {
  const id = String(userKey || '').trim().toLowerCase();
  return id ? `theme:${id}` : 'theme';
};

const readCachedThemeId = (userKey = '') => {
  try {
    const keyed = localStorage.getItem(themeStorageKey(userKey));
    if (keyed) return resolveThemeId(keyed);
    if (!userKey) return resolveThemeId(localStorage.getItem('theme'));
    return DEFAULT_THEME_ID;
  } catch {
    return DEFAULT_THEME_ID;
  }
};

const cacheThemeId = (themeId, userKey = '') => {
  try {
    localStorage.setItem(themeStorageKey(userKey), resolveThemeId(themeId));
  } catch {
    /* ignore */
  }
};

export const ThemeProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const userKey = String(user?.email || user?.id || '').trim().toLowerCase();
  const [themeId, setThemeIdState] = useState(() => {
    const id = readCachedThemeId(userKey);
    applyThemeToDocument(id);
    return id;
  });

  const applyLocal = useCallback(
    (nextId) => {
      const id = resolveThemeId(nextId);
      setThemeIdState(id);
      applyThemeToDocument(id);
      cacheThemeId(id, userKey);
      return id;
    },
    [userKey]
  );

  // Beim User-Wechsel: Cache sofort, dann aus DB laden (pro User)
  useEffect(() => {
    applyLocal(readCachedThemeId(userKey));
    if (!isAuthenticated || !userKey) return undefined;

    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get('/api/theme-preference');
        if (cancelled) return;
        const fromDb = res.data?.theme;
        if (fromDb) {
          applyLocal(fromDb);
          return;
        }
        // Noch kein DB-Eintrag: lokalen Cache einmalig speichern
        const localId = readCachedThemeId(userKey);
        await axios.put('/api/theme-preference', { theme: localId });
        if (!cancelled) applyLocal(localId);
      } catch {
        if (!cancelled) applyLocal(readCachedThemeId(userKey));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, userKey, applyLocal]);

  const setTheme = useCallback(
    (nextId) => {
      const id = applyLocal(nextId);
      if (isAuthenticated && userKey) {
        axios.put('/api/theme-preference', { theme: id }).catch(() => {
          /* offline: Cache bleibt */
        });
      }
    },
    [applyLocal, isAuthenticated, userKey]
  );

  const toggleTheme = useCallback(() => {
    const current = getTheme(themeId);
    setTheme(current.mode === 'dark' ? 'light' : DEFAULT_THEME_ID);
  }, [themeId, setTheme]);

  const theme = getTheme(themeId);
  const isDarkMode = theme.mode === 'dark';

  const value = useMemo(
    () => ({
      themeId: theme.id,
      theme,
      themes: THEME_LIST,
      isDarkMode,
      setTheme,
      toggleTheme,
    }),
    [theme, isDarkMode, setTheme, toggleTheme]
  );

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
